import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Phone, Calendar, TrendingUp, Search, Filter, Users, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin';

const PLAN_COLORS = {
  essenziale: '#6b7280',
  professional: '#c9a84c',
  elite: '#7c3aed',
  lifetime: '#059669',
};
const PLAN_LABELS = {
  essenziale: 'Essenziale', professional: 'Professional', elite: 'Elite', lifetime: 'Lifetime',
};

export function LeadsCrmTab({ storedKey, onComposeToLead }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => { loadLeads(); }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { 'X-Superadmin-Key': storedKey },
      });
      setLeads(data.leads || []);
    } catch {
      toast.error('Errore nel caricamento dei lead.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = leads.filter(l => {
    const matchSearch = !search || [l.name, l.company, l.email].some(v => v?.toLowerCase().includes(search.toLowerCase()));
    const matchPlan = !filterPlan || l.plan_interest === filterPlan;
    const matchStatus = !filterStatus || getDemoStatus(l) === filterStatus;
    return matchSearch && matchPlan && matchStatus;
  });

  function getDemoStatus(lead) {
    if (!lead.demo_slug) return 'lead';
    if (lead.demo_expired) return 'scaduta';
    return 'demo-attiva';
  }

  function StatusBadge({ lead }) {
    const status = getDemoStatus(lead);
    const conf = {
      'lead': { label: 'Lead', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
      'demo-attiva': { label: 'Demo attiva', color: '#059669', bg: 'rgba(5,150,105,0.12)' },
      'scaduta': { label: 'Demo scaduta', color: '#dc2626', bg: 'rgba(220,38,38,0.1)' },
    }[status];
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
        style={{ color: conf.color, background: conf.bg }}>
        {conf.label}
      </span>
    );
  }

  return (
    <div data-testid="leads-crm-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl font-light" style={{ color: 'var(--lux-text)' }}>Lead CRM</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
            {leads.length} lead totali · {leads.filter(l => !l.demo_expired && l.demo_slug).length} demo attive
          </p>
        </div>
        <button onClick={loadLeads} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded"
          style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
          <RefreshCw size={13} /> Aggiorna
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--lux-text-muted-3)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per nome, azienda, email..."
            className="w-full pl-8 pr-4 py-2.5 text-sm rounded-sm"
            style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
            data-testid="leads-search-input" />
        </div>
        <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)}
          className="px-3 py-2.5 text-sm rounded-sm"
          style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
          data-testid="leads-filter-plan">
          <option value="">Tutti i piani</option>
          {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2.5 text-sm rounded-sm"
          style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
          data-testid="leads-filter-status">
          <option value="">Tutti gli stati</option>
          <option value="lead">Solo lead</option>
          <option value="demo-attiva">Demo attiva</option>
          <option value="scaduta">Demo scaduta</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16" style={{ color: 'var(--lux-text-muted)' }}>
          <div className="w-6 h-6 border-2 rounded-full animate-spin mx-auto mb-3"
            style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
          <p className="text-sm">Caricamento lead...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--lux-text-muted)' }}>
          <Users size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">{leads.length === 0 ? 'Nessun lead ancora. I lead appariranno qui dopo le richieste demo dalla landing page.' : 'Nessun risultato per i filtri selezionati.'}</p>
        </div>
      ) : (
        <div className="rounded-sm overflow-hidden" style={{ border: '1px solid var(--lux-border-1)' }}>
          {/* Table Header */}
          <div className="grid text-[10px] uppercase tracking-widest px-5 py-3"
            style={{ gridTemplateColumns: '2fr 2fr 2fr 1fr 1fr auto', background: 'var(--lux-surface-3)', borderBottom: '1px solid var(--lux-border-1)', color: 'var(--lux-text-muted)' }}>
            <span>Nome / Struttura</span>
            <span>Contatti</span>
            <span>Piano interesse</span>
            <span>Stato</span>
            <span>Data</span>
            <span></span>
          </div>
          {/* Rows */}
          {filtered.map((lead, i) => (
            <div key={lead.demo_slug || i}
              className="grid items-center px-5 py-4 transition-colors"
              style={{
                gridTemplateColumns: '2fr 2fr 2fr 1fr 1fr auto',
                borderBottom: i < filtered.length - 1 ? '1px solid var(--lux-border-1)' : 'none',
                background: i % 2 === 0 ? 'var(--lux-bg)' : 'var(--lux-surface-1)',
              }}>
              {/* Nome + Struttura */}
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{lead.name}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>{lead.company}</p>
              </div>
              {/* Contatti */}
              <div className="space-y-1">
                <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-xs hover:underline"
                  style={{ color: 'var(--lux-text-muted)' }}>
                  <Mail size={11} /> {lead.email}
                </a>
                {lead.phone && (
                  <p className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                    <Phone size={11} /> {lead.phone}
                  </p>
                )}
              </div>
              {/* Piano */}
              <div>
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: `${PLAN_COLORS[lead.plan_interest] || '#888'}18`, color: PLAN_COLORS[lead.plan_interest] || '#888' }}>
                  {PLAN_LABELS[lead.plan_interest] || lead.plan_interest}
                </span>
              </div>
              {/* Stato */}
              <div><StatusBadge lead={lead} /></div>
              {/* Data */}
              <div>
                <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                  {lead.created_at ? new Date(lead.created_at).toLocaleDateString('it-IT') : '—'}
                </p>
              </div>
              {/* Azione */}
              <div>
                <button onClick={() => onComposeToLead(lead)}
                  title="Invia email a questo lead"
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-sm"
                  style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--lux-gold)', border: '1px solid rgba(201,168,76,0.2)' }}
                  data-testid={`lead-compose-btn-${i}`}>
                  <Send size={11} /> Email
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
